import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { TOTAL_STEPS } from "@/data/lampContent";
import {
  getFlow,
} from "@/data/volunteerFlows";

/**
 * Sticky horizontal step navbar.
 *
 * Two modes:
 *   • Default (no volunteer): numbered 1..TOTAL_STEPS. Step 1 -> /checklist,
 *     Step N -> /step/N. Retained for the legacy `/step/:no` routes.
 *   • Volunteer mode: renders PHASE buttons (P1..P6) plus a "1" checklist pill
 *     at the start. Active phase is highlighted; earlier phases are marked
 *     done. Clicking a phase button jumps to that phase's intro screen.
 *
 * Props:
 *   activeStep  — number, used in default mode (1..TOTAL_STEPS)
 *   volunteer   — "A" | "B" | "C", switches on volunteer mode
 *   activeIndex — number, current flow index (used in volunteer mode)
 */
export default function StepProgressBar({ activeStep, volunteer }) {
  const navigate = useNavigate();

  if (volunteer && getFlow(volunteer)) {
    return <PhaseBar volunteer={volunteer} />;
  }

  const stepNumbers = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
  const progressPct = Math.round(
    ((activeStep - 1) / (TOTAL_STEPS - 1)) * 100
  );

  const goToStep = (n) => {
    if (n === 1) navigate("/checklist");
    else navigate(`/step/${n}`);
  };

  return (
    <div
      className="sticky top-[77px] z-30 bg-white border-b-2 border-[var(--border-heavy)]/10"
      data-testid="step-progress"
    >
      <div className="mx-auto max-w-3xl px-3 sm:px-6 py-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            className="hidden sm:inline-block font-heading text-xs font-bold uppercase tracking-wider text-[var(--text-muted)] flex-shrink-0 min-w-[92px] tabular-nums"
            data-testid="step-progress-label"
          >
            Step {String(activeStep).padStart(2, "0")}/{String(TOTAL_STEPS).padStart(2, "0")}
          </span>

          <div className="relative flex-1 min-w-0">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 bg-[var(--bg-paper)] rounded-full border border-[var(--border-heavy)]/20"
            />
            <div
              aria-hidden
              className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 bg-[var(--primary)] rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
              data-testid="step-progress-fill"
            />
            <div className="relative flex items-center justify-between gap-1 overflow-x-auto no-scrollbar py-2 px-1">
              {stepNumbers.map((n) => {
                const isActive = n === activeStep;
                const isDone = n < activeStep;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => goToStep(n)}
                    aria-label={`Jump to step ${n}`}
                    aria-current={isActive ? "step" : undefined}
                    data-testid={`step-progress-btn-${n}`}
                    className={`h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-full border-2 font-heading text-xs font-bold shadow-[2px_2px_0px_var(--border-heavy)] transition-colors duration-200 ${
                      isActive
                        ? "bg-[var(--primary)] border-[var(--border-heavy)] text-[var(--text-main)] ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--bg-base)]"
                        : isDone
                          ? "bg-[#f6c445] border-[var(--border-heavy)] text-[#1a1512]"
                          : "bg-white border-[var(--border-heavy)] text-[var(--text-main)] hover:bg-[var(--bg-paper)]"
                    }`}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhaseBar({ volunteer }) {
  const flow = getFlow(volunteer) || [];
  const stepItems = flow.filter((it) => it.type === "step" && it.no !== 8);
  const stepNos = stepItems.map((it) => it.no);
  const [activeNo, setActiveNo] = useState(stepNos[0] || 1);
  const [navH, setNavH] = useState(90);
  const activeBtnRef = useRef(null);

  // Measure the TopNav height (which changes between mobile / desktop breakpoints
  // and can include the arcade caution-tape strip) so this sticky bar always
  // sits FLUSH against its bottom edge — no visible gap when the page scrolls.
  useEffect(() => {
    const nav = document.querySelector('[data-testid="top-nav"]');
    if (!nav) return undefined;
    const update = () =>
      setNavH(Math.max(1, Math.round(nav.getBoundingClientRect().height)));
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(nav);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Watch which step section is currently in view and update the active pill.
  useEffect(() => {
    if (stepNos.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.id; // "step-N"
          const no = Number(id.slice(5));
          if (!Number.isNaN(no)) setActiveNo(no);
        }
      },
      { rootMargin: "-160px 0px -55% 0px", threshold: [0.1, 0.5, 0.9] }
    );
    stepNos.forEach((no) => {
      const el = document.getElementById(`step-${no}`);
      if (el) observer.observe(el);
    });

    // Also treat "scrolled to the bottom of the page" as activating the last step,
    // since the last section may sit below the observer's bottom rootMargin.
    const onScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 40;
      if (nearBottom) setActiveNo(stepNos[stepNos.length - 1]);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [stepNos.join(",")]);

  // Keep the active pill visible on narrow screens.
  useEffect(() => {
    if (activeBtnRef.current) {
      activeBtnRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeNo]);

  const jumpTo = (no) => {
    setActiveNo(no);
    const el = document.getElementById(`step-${no}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Progress fill grows with the active step position.
  const activePos = stepNos.indexOf(activeNo);
  const progressPct =
    stepNos.length <= 1
      ? 0
      : Math.round((Math.max(activePos, 0) / (stepNos.length - 1)) * 100);

  return (
    <div
      className="sticky z-30 bg-white border-b-2 border-[var(--border-heavy)]/10"
      style={{ top: `${navH - 1}px` }}
      data-testid="step-progress"
      data-volunteer-bar={volunteer}
    >
      <div className="mx-auto max-w-3xl px-3 sm:px-6 py-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative flex-1 min-w-0">
            <div
              aria-hidden
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 bg-[var(--bg-paper)] rounded-full border border-[var(--border-heavy)]/20"
            />
            <div
              aria-hidden
              className="absolute left-0 top-1/2 -translate-y-1/2 h-1.5 bg-[var(--primary)] rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
              data-testid="step-progress-fill"
            />
            <div className="relative flex items-center justify-between gap-2 overflow-x-auto no-scrollbar py-2 px-1">
              {stepItems.map((it, pos) => {
                const isActive = it.no === activeNo;
                const isDone = pos < activePos;
                const label = it.label ? it.label : String(it.no);
                return (
                  <button
                    key={it.no}
                    ref={isActive ? activeBtnRef : null}
                    type="button"
                    onClick={() => jumpTo(it.no)}
                    aria-label={`Jump to step ${label}`}
                    aria-current={isActive ? "step" : undefined}
                    data-testid={`step-progress-btn-step-${label}`}
                    className={`h-9 min-w-[36px] flex-shrink-0 px-2 flex items-center justify-center rounded-full border-2 font-heading text-xs font-bold tabular-nums shadow-[2px_2px_0px_var(--border-heavy)] transition-colors duration-200 whitespace-nowrap ${
                      isActive
                        ? "bg-[var(--primary)] border-[var(--border-heavy)] text-[var(--text-main)] ring-2 ring-[var(--accent)] ring-offset-2 ring-offset-[var(--bg-base)]"
                        : isDone
                          ? "bg-[#f6c445] border-[var(--border-heavy)] text-[#1a1512]"
                          : "bg-white border-[var(--border-heavy)] text-[var(--text-main)] hover:bg-[var(--bg-paper)]"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
